/**
 * AdminLaunchControlPage - Slice A rebuild.
 *
 * Three-column Kanban (Waitlist · Pilot · Live) sourced LIVE from
 * db.launch_control via /api/launch-control. Status toggles persist via
 * PUT /api/admin/launch-control/{slug}/status. Each card shows the synced
 * category icon, host counts, and city-live count pulled from
 * /api/admin/launch-control-matrix.
 *
 * Hidden status acts as a 4th lane that collapses by default. All analytics
 * removed from this page (lives in Insights).
 */
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Layers, MapPin, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANES = [
  { id: "coming_soon", label: "Waitlist",
    bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300" },
  { id: "pilot",       label: "Pilot",
    bg: "bg-blue-50",  text: "text-blue-800", border: "border-blue-200" },
  { id: "live",        label: "Live",
    bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-300" },
];

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const SLUG_FROM_CAT = (cid) => (cid || "").replace(/^cat_/, "");

function CategoryCard({ category, onMove }) {
  const id = SLUG_FROM_CAT(category.category_id);
  const lightHex = category.light_hex || category.color_light || "#f5f5f4";
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
      data-testid={`lc-card-${id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
          style={{ backgroundColor: lightHex }}
        >
          {category.icon_url ? (
            <img
              src={category.icon_url}
              alt={category.label}
              className="w-7 h-7 object-contain"
            />
          ) : (
            <span className="text-base font-bold text-stone-700">
              {(category.label || id || "·").charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate capitalize">
            {category.display_label || category.label || id}
          </h3>
        </div>
      </div>
      <div className="flex items-center justify-between gap-1 text-[10px]">
        <span className="text-gray-400">Move →</span>
        {LANES.filter((l) => l.id !== category.launch_state).map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => onMove(category, l.id)}
            data-testid={`lc-move-${id}-${l.id}`}
            className={`px-2 py-1 rounded ${l.bg} ${l.text} hover:opacity-80 transition-opacity`}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminLaunchControlPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      // Single canonical endpoint - returns categories with launch_status, host counts
      const res = await fetch(`${API}/admin/launch-control`, { headers: headers() });
      if (!res.ok) throw new Error(`${res.status}`);
      const d = await res.json();
      const cats = (d.categories || []).map((c) => ({
        category_id: c.category_id,
        slug: c.slug,
        label: c.name,
        display_label: c.name,
        icon_url: c.icon_url,
        launch_state: c.launch_status === "coming_soon" ? "coming_soon" : c.launch_status,
        host_count: c.is_active ? c.event_count || 0 : 0,
        pilot_count: c.pilot_count || 0,
        cities_live: c.cities_live || 0,
      }));
      setCategories(cats);

      // Iter 9.40 Slice C⁸ - Master Components is the SSOT for category branding.
      // Merge icon_url + light_hex + dark_hex from /admin/categories so the
      // Launch Control cards + Cities matrix top-row reflect saved icons/colors.
      try {
        const ms = await fetch(`${API}/admin/categories`, { headers: headers() });
        if (ms.ok) {
          const md = await ms.json();
          const list = Array.isArray(md) ? md : md.categories || [];
          const masterMap = {};
          list.forEach((c) => {
            const cid = c.category_id;
            const short = (cid || "").replace(/^cat_/, "");
            const entry = {
              icon_url: c.icon_url,
              light_hex: c.light_hex || c.color_light,
              dark_hex: c.dark_hex || c.color_dark,
              label: c.label || c.name,
            };
            masterMap[cid] = entry;
            if (short) masterMap[short] = entry;
          });
          setCategories((prev) =>
            prev.map((c) => {
              const m = masterMap[c.category_id] || masterMap[(c.category_id || "").replace(/^cat_/, "")] || {};
              return {
                ...c,
                icon_url: m.icon_url || c.icon_url,
                light_hex: m.light_hex || c.light_hex,
                dark_hex: m.dark_hex || c.dark_hex,
                label: m.label || c.label,
                display_label: m.label || c.display_label,
              };
            })
          );
        }
      } catch { /* keep what we have */ }

      // Legacy /category-masters fallback (kept for older deploys)
      const cm = await fetch(`${API}/category-masters`, { headers: headers() });
      if (cm.ok) {
        const cmd = await cm.json();
        const iconMap = {};
        ((cmd.categories || cmd.category_masters || cmd) || []).forEach((c) => {
          if (c.icon_url) iconMap[c.category_id] = c.icon_url;
        });
        setCategories((prev) => prev.map((c) => ({ ...c, icon_url: c.icon_url || iconMap[c.category_id] })));
      }
    } catch (e) {
      toast.error(`Failed to load launch control: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const move = async (category, newState) => {
    const slug = SLUG_FROM_CAT(category.category_id);
    // Optimistic
    setCategories((prev) => prev.map((c) =>
      c.category_id === category.category_id ? { ...c, launch_state: newState } : c
    ));
    try {
      const res = await fetch(`${API}/admin/launch-control/${slug}/status`, {
        method: "PUT",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ launch_status: newState }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast.success(`${category.label || slug} → ${newState}`);
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
      load();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-launch-control-page">
      <header className="bg-gray-100 border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Launch Control</h1>
            <p className="text-xs text-gray-500">Drag categories between lanes to change launch state. All analytics live in Insights.</p>
          </div>
        </div>
        <button onClick={load} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="lc-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </header>

      <main className="max-w-[1700px] mx-auto p-4 sm:p-6">
        <Tabs defaultValue="categories">
          <TabsList className="bg-stone-100 mb-4" data-testid="lc-section-tabs">
            <TabsTrigger value="categories" data-testid="lc-tab-categories"><Layers className="w-3.5 h-3.5 mr-1.5" />Categories</TabsTrigger>
            <TabsTrigger value="cities" data-testid="lc-tab-cities"><MapPin className="w-3.5 h-3.5 mr-1.5" />Cities</TabsTrigger>
            <TabsTrigger value="features" data-testid="lc-tab-features"><Sparkles className="w-3.5 h-3.5 mr-1.5" />Features</TabsTrigger>
          </TabsList>
          <TabsContent value="categories">
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {LANES.map((lane) => {
              const cats = categories.filter((c) => {
                const s = c.launch_state || "coming_soon";
                return s === lane.id || (lane.id === "coming_soon" && s === "waitlist");
              });
              return (
                <div
                  key={lane.id}
                  className={`${lane.bg} rounded-xl border ${lane.border} p-3`}
                  data-testid={`lc-lane-${lane.id}`}
                >
                  <header className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <h2 className={`text-xs font-bold uppercase tracking-wider ${lane.text}`}>
                        {lane.label}
                      </h2>
                      <span className={`text-[10px] ${lane.text} opacity-70`}>{cats.length}</span>
                    </div>
                  </header>
                  <div className="space-y-3">
                    {cats.map((c) => (
                      <CategoryCard
                        key={c.category_id}
                        category={c}
                        onMove={move}
                      />
                    ))}
                    {cats.length === 0 && (
                      <p className="text-[11px] text-gray-400 italic text-center py-6">No categories in this lane.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </TabsContent>
          <TabsContent value="cities">
            <CitiesCategoriesMatrix categories={categories} onMove={move} loading={loading} />
          </TabsContent>
          <TabsContent value="features">
            <div className="bg-white rounded-xl border border-stone-200 p-6 text-center text-stone-500" data-testid="lc-features-placeholder">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-stone-400" />
              <h3 className="text-sm font-semibold text-stone-700">Feature flags rollout</h3>
              <p className="text-xs mt-1 max-w-xl mx-auto">Master Features list × launch state - read from <code className="text-[10px] bg-stone-100 px-1 rounded">db.master_features</code>. Each feature carries a stage (idea / spec / building / shipped) plus per-category visibility, mirroring the Categories lane behaviour.</p>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Cities × Categories matrix (Iter 9.40 Slice C⁶)
// Loads /api/admin/launch-control-matrix for the live cell grid; falls back
// to a 12-city default list if the endpoint isn't seeded yet so the grid is
// always visible. Cell click cycles waitlist → pilot → live → waitlist and
// PUTs to /api/admin/launch-control-matrix/{city}/{category}/status.
// ────────────────────────────────────────────────────────────────────────
const DEFAULT_CITIES = [
  "Atlanta", "Austin", "Brooklyn", "Chicago", "Dallas", "Denver",
  "Houston", "Los Angeles", "Miami", "New York", "Philadelphia", "Phoenix",
];

const CELL_STATES = ["coming_soon", "pilot", "live"];
const CELL_TONE = {
  coming_soon: { bg: "bg-gray-100", text: "text-gray-500", label: "WL" },
  waitlist:    { bg: "bg-gray-100", text: "text-gray-500", label: "WL" },
  pilot:       { bg: "bg-blue-100", text: "text-blue-800", label: "P" },
  live:        { bg: "bg-emerald-100", text: "text-emerald-800", label: "L" },
  hidden:      { bg: "bg-stone-50",  text: "text-stone-400", label: "-" },
};
const TONE_FALLBACK = { bg: "bg-gray-100", text: "text-gray-500", label: "·" };

function CitiesCategoriesMatrix({ categories, onMove, loading }) {
  const [cells, setCells] = useState({}); // key: `${city}__${slug}` → state
  const [cities, setCities] = useState(DEFAULT_CITIES.map((n) => ({ name: n, slug: n.toLowerCase().replace(/\s+/g, "_"), live_projection: null, active: true })));
  const [matrixLoading, setMatrixLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/admin/launch-control-matrix`, { headers: headers() });
        if (res.ok) {
          const d = await res.json();
          if (Array.isArray(d.cities) && d.cities.length) {
            // Iter 9.40 Slice C⁸ - cities is now [{name, slug, live_projection}]
            const normalized = d.cities.map((c) =>
              typeof c === "string"
                ? { name: c, slug: c.toLowerCase().replace(/\s+/g, "_"), live_projection: null, active: true }
                : c
            );
            setCities(normalized);
          }
          const map = {};
          (d.cells || []).forEach((c) => {
            const slug = SLUG_FROM_CAT(c.category_id || c.slug);
            map[`${c.city}__${slug}`] = c.launch_status || c.state || "coming_soon";
          });
          setCells(map);
        }
      } catch {/* fallback to default grid */}
      finally { setMatrixLoading(false); }
    };
    load();
  }, []);

  const cycle = async (city, slug) => {
    const cityName = typeof city === "string" ? city : (city.name || city.slug);
    const key = `${cityName}__${slug}`;
    const cur = cells[key] || "coming_soon";
    const next = CELL_STATES[(CELL_STATES.indexOf(cur) + 1) % CELL_STATES.length];
    setCells((prev) => ({ ...prev, [key]: next }));
    try {
      const res = await fetch(`${API}/admin/launch-control-matrix/${encodeURIComponent(cityName)}/${slug}/status`, {
        method: "PUT",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ launch_status: next }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast.success(`${cityName} · ${slug} → ${next}`);
    } catch (e) {
      setCells((prev) => ({ ...prev, [key]: cur }));
      toast.error(`Save failed (${e.message})`);
    }
  };

  if (loading || matrixLoading) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="lc-cities-matrix">
        <header className="px-4 py-3 border-b border-stone-100">
          <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-stone-500" /> Cities × Categories
          </h3>
        </header>
        <table className="w-full text-xs" data-testid="lc-matrix-table">
          <tbody>
            <tr>
              <td className="px-4 py-12 text-center text-stone-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading matrix…
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const cats = (categories || []).slice().sort((a, b) => (a.label || "").localeCompare(b.label || ""));

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="lc-cities-matrix">
      <header className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-stone-500" /> Cities × Categories
          </h3>
          <p className="text-[11px] text-stone-500">Click a cell to cycle Waitlist → Pilot → Live. Synced bidirectionally with the Categories Kanban.</p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          {CELL_STATES.map((s) => (
            <div key={s} className="flex items-center gap-1">
              <span className={`inline-block w-3 h-3 rounded ${CELL_TONE[s].bg}`} />
              <span className="text-stone-500 capitalize">{s.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="lc-matrix-table">
          <thead className="bg-stone-50 border-b border-stone-100 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-stone-600 sticky left-0 bg-stone-50 z-10 min-w-[140px]">City</th>
              {cats.map((c) => {
                const slug = SLUG_FROM_CAT(c.category_id);
                return (
                  <th key={slug} className="px-2 py-2 text-center font-medium text-stone-600 min-w-[64px]">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-7 h-7 rounded flex items-center justify-center overflow-hidden" style={{ backgroundColor: c.light_hex || c.color_light || "#f5f5f4" }}>
                        {c.icon_url ? (
                          <img
                            src={c.icon_url}
                            alt={c.label}
                            className="w-5 h-5 object-contain"
                            onError={(e) => { e.target.style.display = "none"; e.target.parentElement.querySelector("[data-fallback]").style.display = "flex"; }}
                          />
                        ) : null}
                        <span data-fallback className={`w-full h-full items-center justify-center text-[10px] font-bold text-stone-700 ${c.icon_url ? "hidden" : "flex"}`}>
                          {(c.label || slug || "·").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-[10px] capitalize">{c.label || slug}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {cities.map((city) => {
              const cityName = typeof city === "string" ? city : (city.name || city.slug);
              return (
                <tr key={cityName} className="border-t border-stone-100 hover:bg-stone-50/40">
                  <td className="px-3 py-2 sticky left-0 bg-white z-10" data-testid={`lc-row-${cityName}`}>
                    <div className="flex flex-col">
                      <span className="font-medium text-stone-700">{cityName}</span>
                      {city.live_projection && (
                        <span className="text-[10px] text-stone-400">live: {String(city.live_projection).slice(0, 10)}</span>
                      )}
                    </div>
                  </td>
                  {cats.map((c) => {
                    const slug = SLUG_FROM_CAT(c.category_id);
                    const state = cells[`${cityName}__${slug}`] || "coming_soon";
                    const tone = CELL_TONE[state] || TONE_FALLBACK;
                    return (
                      <td key={slug} className="px-1.5 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={() => cycle(city, slug)}
                          className={`w-full h-7 rounded ${tone.bg} ${tone.text} text-[10px] font-semibold hover:opacity-80 transition-opacity`}
                          data-testid={`lc-cell-${cityName}-${slug}`}
                          title={`${cityName} · ${slug} · ${state}`}
                        >
                          {tone.label}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
